
import React from 'react';

interface LogoProps {
  className?: string;
  light?: boolean;
}

/**
 * Groflex Official Brand Identity Component
 * 
 * High-fidelity rendering of the Groflex logo using the provided brand asset.
 * - light: Optimizes visibility for dark 'Zodiac' backgrounds (e.g., in the Navbar or Footer).
 * - Performance: Uses hardware-accelerated transforms for fluid micro-interactions.
 * - Fidelity: Implements contrast-optimized rendering to maintain brand sharpness.
 */
export const Logo: React.FC<LogoProps> = ({ className = "h-8", light = false }) => {
  return (
    <div className={`relative flex items-center select-none group transition-executive cursor-pointer ${className}`}>
      {/* Container for the logo image with hardware acceleration layers */}
      <div className="relative h-full flex items-center overflow-hidden">
        <img 
          src="logo.png" 
          alt="Groflex" 
          className={`h-full w-auto object-contain transition-all duration-700 ease-out transform group-hover:scale-[1.04] ${
            light 
              ? 'brightness-0 invert opacity-90 group-hover:opacity-100' 
              : 'opacity-100 group-hover:brightness-110'
          }`}
          style={{
            // Keeps the brand asset sharp across all device resolutions
            imageRendering: 'auto',
            // Optimizes for paint performance
            willChange: 'transform, filter'
          }}
        />
        
        {/* Executive Shimmer: A refined lens-sweep effect that tracks across the logo on hover */}
        <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute top-0 -left-[100%] w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-30deg] group-hover:animate-[shimmer_1.5s_ease-in-out_infinite]"></div>
        </div>
      </div>
      
      {/* Strategic Glow: Adds a subtle atmospheric accent to the logo when used on dark backgrounds */}
      {light && (
        <div className="absolute inset-0 bg-accent/5 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none"></div>
      )}
    </div>
  );
};
